import { createFileRoute } from "@tanstack/react-router";
import { useState } from "react";
import { z } from "zod";
import { Check } from "lucide-react";
import { useServerFn } from "@tanstack/react-start";
import { useEntry, useEntries, useField } from "@/lib/content";
import { submitApplication } from "@/lib/applications.functions";
import grillFlames from "@/assets/grill-flames.jpg";
import boxIcon from "@/assets/icons/box.svg";
import chartUserIcon from "@/assets/icons/chart-user.svg";
import boltIcon from "@/assets/icons/bolt.svg";
import earthIcon from "@/assets/icons/earth.svg";


const SITE_URL = "https://tacoya.snapinsolutions.com";

export const Route = createFileRoute("/franchise")({
  head: () => ({
    meta: [
      { title: "Franchise — Bring Tacoya To Your City" },
      { name: "description", content: "Tacoya is scaling across the Nordics. Small footprint, viral brand, franchise-ready operations. Apply now." },
      { property: "og:title", content: "Franchise With Tacoya" },
      { property: "og:description", content: "Bring the Nordic street food movement to your city." },
      { property: "og:url", content: `${SITE_URL}/franchise` },
    ],
    links: [{ rel: "canonical", href: `${SITE_URL}/franchise` }],
    scripts: [
      {
        type: "application/ld+json",
        children: JSON.stringify({
          "@context": "https://schema.org",
          "@type": "BreadcrumbList",
          itemListElement: [
            { "@type": "ListItem", position: 1, name: "Home", item: `${SITE_URL}/` },
            { "@type": "ListItem", position: 2, name: "Franchise", item: `${SITE_URL}/franchise` },
          ],
        }),
      },
    ],
  }),
  component: FranchisePage,
});

const schema = z.object({
  name: z.string().trim().min(1, "Required").max(100),
  email: z.string().trim().email("Invalid email").max(255),
  city: z.string().trim().min(1, "Required").max(100),
  message: z.string().trim().min(10, "Tell us a bit more").max(1000),
});

const PILLAR_ICONS: Record<string, string> = {
  box: boxIcon,
  "chart-user": chartUserIcon,
  bolt: boltIcon,
  earth: earthIcon,
};

function FranchisePage() {
  return (
    <div>
      <FranchiseHero />
      <Pillars />
      <Roadmap />
      <Form />
    </div>
  );
}

function FranchiseHero() {
  const f = useField();
  const hero = useEntry("franchise", "hero");
  const stats = useEntries("franchise", "hero_stats");

  return (
    <section className="relative min-h-[88vh] flex items-end overflow-hidden grain pt-16 md:pt-40">
      <img src={hero?.image ?? grillFlames} alt="" className="absolute inset-0 w-full h-full object-cover scale-105" />
      <div className="absolute inset-0 bg-gradient-to-t from-charcoal via-charcoal/75 to-charcoal/30" />
      <div className="absolute inset-0 bg-[radial-gradient(60%_50%_at_20%_80%,color-mix(in_oklab,var(--chili)_25%,transparent),transparent_70%)]" />

      {/* Flicker glow + rising embers */}
      <div aria-hidden className="pointer-events-none absolute inset-0 mix-blend-screen opacity-70 animate-[fireFlicker_3.2s_ease-in-out_infinite] bg-[radial-gradient(40%_30%_at_30%_85%,color-mix(in_oklab,var(--chili)_45%,transparent),transparent_70%),radial-gradient(35%_25%_at_70%_90%,color-mix(in_oklab,var(--yellow)_35%,transparent),transparent_70%)]" />
      <div aria-hidden className="pointer-events-none absolute inset-0 overflow-hidden">
        {Array.from({ length: 18 }).map((_, i) => (
          <span
            key={i}
            className="absolute bottom-0 size-1 rounded-full bg-yellow shadow-[0_0_8px_2px_color-mix(in_oklab,var(--chili)_70%,transparent)] animate-[ember_var(--d)_linear_infinite]"
            style={{
              left: `${(i * 53) % 100}%`,
              ['--d' as string]: `${4 + (i % 5)}s`,
              animationDelay: `${(i * 0.4) % 6}s`,
              opacity: 0.6 + ((i % 4) * 0.1),
            }}
          />
        ))}
      </div>

      <div className="relative mx-auto max-w-7xl px-4 sm:px-5 md:px-8 pb-16 md:pb-24 lg:pb-28 w-full">
        <div className="grid lg:grid-cols-12 gap-10 items-end">
          <div className="lg:col-span-9 xl:col-span-8">
            <h1 className="relative font-display text-[11vw] sm:text-[10vw] md:text-7xl lg:text-8xl xl:text-[7.5rem] leading-[0.95] tracking-tight text-cream [text-shadow:0_0_30px_color-mix(in_oklab,var(--chili)_55%,transparent),0_0_80px_color-mix(in_oklab,var(--chili)_30%,transparent)] animate-[fireFlicker_3.6s_ease-in-out_infinite]">
              {f(hero, "heading_a", "BRING TACOYA")}<br /><span className="text-yellow inline-block mt-2 md:mt-3 [text-shadow:0_0_28px_color-mix(in_oklab,var(--yellow)_70%,transparent),0_0_70px_color-mix(in_oklab,var(--chili)_55%,transparent)]">{f(hero, "heading_b", "TO YOUR CITY.")}</span>
            </h1>



            <p className="mt-6 max-w-2xl text-base md:text-lg lg:text-xl text-cream/85 leading-relaxed">
              {f(hero, "blurb")}
            </p>

            <div className="mt-10 flex flex-col sm:flex-row sm:items-center gap-6 sm:gap-8">
              <a
                href={String(hero?.data.cta_href ?? "#apply")}
                className="group relative inline-flex items-center gap-3 bg-yellow text-charcoal pl-7 pr-3 py-2.5 text-sm uppercase tracking-[0.18em] font-bold rounded-full hover:bg-cream transition-colors shadow-[0_18px_40px_-12px_color-mix(in_oklab,var(--yellow)_60%,transparent)]"
              >
                <span>{f(hero, "cta_label", "Start the conversation")}</span>
                <span className="flex items-center justify-center size-10 rounded-full bg-charcoal text-yellow group-hover:bg-chili group-hover:text-cream transition-colors">
                  →
                </span>
              </a>

              <div className="hidden sm:flex items-center gap-6 px-5 py-3 rounded-full border border-cream/10 bg-charcoal/40 backdrop-blur-md">
                {stats.map((s, i) => (
                  <div key={s.key} className="flex items-center gap-6">
                    <div className="leading-tight">
                      <div className="font-display text-xl text-yellow">{f(s, "value")}</div>
                      <div className="text-[9px] uppercase tracking-[0.12em] text-cream/55">{f(s, "label")}</div>
                    </div>
                    {i < stats.length - 1 && <span className="h-7 w-px bg-cream/15" />}
                  </div>
                ))}
              </div>
            </div>
          </div>

          {/* Desktop side card */}
          <aside className="hidden lg:flex lg:col-span-3 xl:col-span-4 justify-end">
            <div className="w-full max-w-xs rounded-3xl border border-cream/10 bg-charcoal/45 backdrop-blur-xl p-6 shadow-[0_30px_60px_-30px_rgba(0,0,0,0.9)]">
              <div className="inline-flex items-center gap-2 px-2.5 py-1 rounded-full border border-yellow/30 bg-yellow/10">
                <span className="size-1.5 rounded-full bg-yellow animate-pulse" />
                <span className="text-[9px] uppercase tracking-[0.15em] text-yellow font-bold">{f(hero, "card_badge", "Now opening")}</span>
              </div>
              <h3 className="mt-4 font-display text-2xl text-cream leading-tight uppercase">{f(hero, "card_title", "2026 Franchise Cohort")}</h3>
              <p className="mt-2 text-sm text-cream/65 leading-relaxed">
                {f(hero, "card_blurb")}
              </p>
              <div className="mt-5 pt-5 border-t border-cream/10 flex items-center justify-between text-[10px] uppercase tracking-[0.12em] text-cream/55">
                <span>{f(hero, "card_apply_label", "Apply by")}</span>
                <span className="text-yellow font-bold">{f(hero, "card_apply_value", "Q2 2026")}</span>
              </div>
            </div>
          </aside>
        </div>
      </div>
    </section>
  );
}


function Pillars() {
  const f = useField();
  const heading = useEntry("franchise", "pillars", "heading");
  const items = useEntries("franchise", "pillars_items");

  return (
    <section className="py-16 md:py-28 mx-auto max-w-7xl px-4 sm:px-5 md:px-8">
      <h2 className="font-display text-4xl sm:text-5xl md:text-7xl text-cream leading-[1.05] mb-12">
        {f(heading, "heading_a", "WHY")} <span className="text-chili">{f(heading, "heading_b", "TACOYA.")}</span>
      </h2>

      <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-4 md:gap-5">
        {items.map((it) => {
          const icon = PILLAR_ICONS[String(it.data.icon ?? "box")] ?? boxIcon;
          return (
            <div
              key={it.key}
              className="group relative overflow-hidden rounded-2xl border border-cream/10 bg-gradient-to-br from-cream/[0.08] via-cream/[0.03] to-transparent backdrop-blur-2xl p-7 md:p-8 shadow-[inset_0_1px_0_0_rgba(255,255,255,0.08),0_30px_60px_-30px_rgba(0,0,0,0.9)] transition-all duration-500 hover:border-yellow/40 hover:-translate-y-1 hover:shadow-[inset_0_1px_0_0_rgba(255,255,255,0.12),0_40px_80px_-30px_rgba(0,0,0,0.95)]"
            >
              {/* top sheen */}
              <span aria-hidden className="pointer-events-none absolute inset-x-8 top-0 h-px bg-gradient-to-r from-transparent via-cream/40 to-transparent" />
              {/* corner glow */}
              <span aria-hidden className="pointer-events-none absolute -top-16 -right-16 size-40 rounded-full bg-yellow/10 blur-3xl opacity-0 group-hover:opacity-100 transition-opacity duration-500" />
              {/* base radial */}
              <span aria-hidden className="pointer-events-none absolute inset-0 bg-[radial-gradient(80%_60%_at_0%_0%,color-mix(in_oklab,var(--yellow)_8%,transparent),transparent_60%)] opacity-60" />

              <div className="relative">
                <span className="inline-flex items-center justify-center size-12 rounded-xl border border-yellow/25 bg-gradient-to-br from-yellow/15 to-yellow/[0.03] shadow-[inset_0_1px_0_0_rgba(255,255,255,0.1)]">
                  <img src={icon} alt="" className="size-5 [filter:brightness(0)_saturate(100%)_invert(82%)_sepia(57%)_saturate(580%)_hue-rotate(2deg)_brightness(102%)_contrast(95%)]" />
                </span>

                <h3 className="mt-6 text-2xl md:text-3xl text-cream leading-tight font-bold" style={{ fontFamily: "'Poppins', sans-serif" }}>{f(it, "title")}</h3>
                <p className="mt-3 text-sm text-cream/65 leading-relaxed" style={{ fontFamily: "'Poppins', sans-serif" }}>{f(it, "desc")}</p>

              </div>
            </div>
          );
        })}
      </div>
    </section>
  );
}

function Roadmap() {
  const f = useField();
  const heading = useEntry("franchise", "roadmap", "heading");
  const phases = useEntries("franchise", "roadmap_items");
  const activeIndex = phases.findIndex((p) => p.data.status === "active");
  const fillWidth = phases.length > 1 && activeIndex >= 0
    ? `${(activeIndex / (phases.length - 1)) * 100 * 0.8 + 10 - 10}%`
    : "40%";

  return (
    <section className="relative py-20 md:py-32 bg-cream text-charcoal overflow-hidden">
      {/* ambient tint */}
      <div aria-hidden className="pointer-events-none absolute inset-0">
        <div className="absolute -top-32 -left-24 w-[420px] h-[420px] rounded-full bg-chili/10 blur-3xl" />
        <div className="absolute -bottom-32 -right-24 w-[480px] h-[480px] rounded-full bg-yellow/20 blur-3xl" />
      </div>

      <div className="relative mx-auto max-w-7xl px-4 sm:px-5 md:px-8">
        {/* header */}
        <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-6 mb-14 md:mb-20">
          <div>
            <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full border border-chili/30 bg-chili/10 mb-5">
              <span className="size-1.5 rounded-full bg-chili animate-pulse" />
              <span className="text-[10px] uppercase tracking-[0.15em] text-chili font-bold">{f(heading, "kicker", "Roadmap · 2022 → 2027")}</span>
            </div>
            <h2 className="font-display text-4xl sm:text-5xl md:text-7xl lg:text-8xl leading-[1.05]">
              {f(heading, "heading_a", "THE")} <span className="text-chili">{f(heading, "heading_b", "EXPANSION.")}</span>
            </h2>

          </div>
          <div className="hidden md:flex items-center gap-6 text-[10px] uppercase tracking-[0.12em] text-charcoal/55">
            <span className="flex items-center gap-2"><span className="size-2 rounded-full bg-charcoal/30" /> Done</span>
            <span className="flex items-center gap-2"><span className="size-2 rounded-full bg-chili animate-pulse" /> Now</span>
            <span className="flex items-center gap-2"><span className="size-2 rounded-full border border-charcoal/30" /> Next</span>
          </div>
        </div>

        {/* TIMELINE */}
        <div className="relative">
          {/* desktop spine — sits exactly on the node centers (top:14px = half of size-7) */}
          <div aria-hidden className="hidden md:block absolute top-3.5 left-[10%] right-[10%] h-px bg-charcoal/15" />
          {/* progress fill — reaches center of "active" node */}
          <div aria-hidden className="hidden md:block absolute top-3.5 left-[10%] h-px bg-gradient-to-r from-chili via-chili to-chili/70" style={{ width: fillWidth }} />

          <ol className="grid md:grid-cols-5 gap-8 md:gap-5">
            {phases.map((p, i) => {
              const status = String(p.data.status ?? "next");
              return (
                <li key={p.key} className="relative group">
                  <div className="flex md:flex-col md:items-stretch items-start gap-4 md:gap-0">
                    {/* node — centered in column on desktop */}
                    <div className="relative md:self-center">
                      <span
                        className={[
                          "relative z-10 flex items-center justify-center size-7 rounded-full ring-4 ring-cream shrink-0 transition-transform group-hover:scale-110",
                          status === "done" && "bg-charcoal text-cream",
                          status === "active" && "bg-chili text-cream shadow-[0_0_0_6px_color-mix(in_oklab,var(--chili)_18%,transparent)]",
                          status === "next" && "bg-cream border-2 border-charcoal/25 text-charcoal/50",
                        ].filter(Boolean).join(" ")}
                        aria-hidden
                      >
                        <span className="font-mono text-[10px] font-bold leading-none">{String(i + 1).padStart(2, "0")}</span>
                      </span>
                      {/* mobile vertical connector */}
                      {i < phases.length - 1 && (
                        <span aria-hidden className="md:hidden absolute left-1/2 top-7 -translate-x-1/2 w-px h-[calc(100%+2rem)] bg-charcoal/15" />
                      )}
                    </div>

                    {/* card */}
                    <div className="flex-1 md:mt-7 w-full rounded-2xl border border-charcoal/10 bg-charcoal/[0.02] p-5 transition-all duration-300 group-hover:-translate-y-1 group-hover:border-chili/30 group-hover:bg-cream group-hover:shadow-[0_20px_40px_-20px_rgba(0,0,0,0.25)]">
                      <div className="flex items-center justify-between mb-2">
                        <span className="font-display text-3xl md:text-4xl text-chili leading-none">{f(p, "year")}</span>
                        {status === "active" && (
                          <span className="text-[9px] uppercase tracking-[0.12em] font-bold text-chili px-2 py-1 rounded-full bg-chili/10 border border-chili/30">
                            Now
                          </span>
                        )}
                      </div>
                      <h3 className="mt-3 font-bold uppercase tracking-[0.15em] text-[13px] text-charcoal">{f(p, "title")}</h3>
                      <p className="mt-2 text-[13px] text-charcoal/65 leading-relaxed">{f(p, "desc")}</p>
                    </div>
                  </div>
                </li>
              );
            })}
          </ol>
        </div>
      </div>
    </section>
  );
}

function Form() {
  const f = useField();
  const form = useEntry("franchise", "form");
  const [data, setData] = useState({ name: "", email: "", city: "", message: "" });
  const [status, setStatus] = useState<"idle" | "submitting" | "ok" | "error">("idle");
  const [errors, setErrors] = useState<Record<string, string>>({});
  const submitFn = useServerFn(submitApplication);

  const bullets = [
    f(form, "bullet1", "Reply within 5 business days"),
    f(form, "bullet2", "Discovery call with the founders"),
    f(form, "bullet3", "Site visit · Helsinki HQ"),
  ];

  async function onSubmit(e: React.FormEvent) {
    e.preventDefault();
    const r = schema.safeParse(data);
    if (!r.success) {
      const errs: Record<string, string> = {};
      r.error.issues.forEach((i) => { errs[i.path[0] as string] = i.message; });
      setErrors(errs);
      setStatus("error");
      return;
    }
    setErrors({});
    setStatus("submitting");
    try {
      await submitFn({ data: r.data });
      setStatus("ok");
    } catch {
      setStatus("error");
      setErrors({ message: "Something went wrong. Please try again." });
    }
  }


  return (
    <section id="apply" className="relative py-20 md:py-32 overflow-hidden">
      {/* ambient background */}
      <div aria-hidden className="pointer-events-none absolute inset-0">
        <div className="absolute -top-40 -left-32 w-[520px] h-[520px] rounded-full bg-chili/15 blur-3xl" />
        <div className="absolute -bottom-40 -right-32 w-[560px] h-[560px] rounded-full bg-yellow/10 blur-3xl" />
        <div className="absolute inset-0 opacity-[0.025]" style={{ backgroundImage: "linear-gradient(var(--cream) 1px, transparent 1px), linear-gradient(90deg, var(--cream) 1px, transparent 1px)", backgroundSize: "80px 80px" }} />
      </div>

      <div className="relative mx-auto max-w-6xl px-4 sm:px-5 md:px-8 grid lg:grid-cols-[1fr_1.2fr] gap-10 lg:gap-16 items-start">
        {/* LEFT — pitch column */}
        <div className="lg:sticky lg:top-28">
          <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full border border-yellow/30 bg-yellow/10 backdrop-blur-md">
            <span className="size-1.5 rounded-full bg-yellow animate-pulse" />
            <span className="text-[10px] uppercase tracking-[0.15em] text-yellow font-bold">{f(form, "kicker", "Apply · 2026 Cohort")}</span>
          </div>
          <h2 className="mt-6 font-display text-4xl sm:text-5xl md:text-6xl lg:text-7xl text-cream leading-[1.05]">
            {f(form, "heading_a", "TELL US ABOUT")}<br /><span className="text-yellow inline-block mt-2">{f(form, "heading_b", "YOUR CITY.")}</span>
          </h2>

          <p className="mt-6 max-w-md text-cream/70 leading-relaxed">
            {f(form, "blurb")}
          </p>

          <ul className="mt-8 space-y-3 text-sm text-cream/75">
            {bullets.map((t) => (
              <li key={t} className="flex items-center gap-3">
                <span className="flex items-center justify-center size-6 rounded-full border border-yellow/40 bg-yellow/10 text-yellow">
                  <Check size={12} />
                </span>
                <span>{t}</span>
              </li>
            ))}
          </ul>
        </div>

        {/* RIGHT — glass card */}
        <div className="relative">
          {/* glass card */}
          <div className="relative overflow-hidden rounded-3xl border border-cream/10 bg-gradient-to-br from-cream/[0.08] via-cream/[0.03] to-transparent backdrop-blur-2xl p-6 sm:p-10 shadow-[inset_0_1px_0_0_rgba(255,255,255,0.1),0_40px_80px_-30px_rgba(0,0,0,0.95)]">
            {/* sheen */}
            <span aria-hidden className="pointer-events-none absolute inset-x-10 top-0 h-px bg-gradient-to-r from-transparent via-cream/40 to-transparent" />
            <span aria-hidden className="pointer-events-none absolute -top-24 -right-24 size-56 rounded-full bg-yellow/10 blur-3xl" />

            {status === "ok" ? (
              <div className="relative flex items-start gap-4">
                <span className="flex items-center justify-center size-12 rounded-full border border-yellow/40 bg-yellow/15 text-yellow shrink-0">
                  <Check />
                </span>
                <div>
                  <h3 className="font-display text-3xl text-cream">{f(form, "success_title", "Message received.")}</h3>
                  <p className="mt-2 text-cream/70">{f(form, "success_text", "We'll be in touch within a few days. Get ready.")}</p>
                </div>
              </div>
            ) : (
              <form onSubmit={onSubmit} className="relative grid gap-5" noValidate>
                <div className="flex items-center justify-between">
                  <span className="text-[10px] uppercase tracking-[0.15em] text-cream/45">Step 01 / Application</span>
                  
                </div>

                <div className="grid sm:grid-cols-2 gap-5">
                  <Field label={f(form, "field_name", "Your name")} error={errors.name}>
                    <input value={data.name} onChange={(e) => setData({ ...data, name: e.target.value })} className="input" maxLength={100} placeholder="Jane Doe" />
                  </Field>
                  <Field label={f(form, "field_email", "Email")} error={errors.email}>
                    <input type="email" value={data.email} onChange={(e) => setData({ ...data, email: e.target.value })} className="input" maxLength={255} placeholder="you@example.com" />
                  </Field>
                </div>

                <Field label={f(form, "field_city", "Target city")} error={errors.city}>
                  <input value={data.city} onChange={(e) => setData({ ...data, city: e.target.value })} className="input" maxLength={100} placeholder="Stockholm, Oslo, Copenhagen…" />
                </Field>

                <Field label={f(form, "field_message", "Tell us about your background")} error={errors.message} hint={`${data.message.length} / 1000`}>
                  <textarea value={data.message} onChange={(e) => setData({ ...data, message: e.target.value })} className="input min-h-36 resize-none" maxLength={1000} placeholder="F&B operator, investor, multi-unit experience…" />
                </Field>

                <div className="flex flex-col sm:flex-row items-stretch sm:items-center gap-3 sm:justify-between mt-2">
                  <p className="text-[11px] text-cream/45 leading-relaxed">
                    {f(form, "consent", "By submitting, you agree to be contacted about Tacoya partnerships.")}
                  </p>
                  <button type="submit" disabled={status === "submitting"} className="group relative inline-flex items-center justify-center gap-2 bg-chili text-cream px-7 py-4 text-sm uppercase tracking-widest font-bold rounded-full hover:bg-yellow hover:text-charcoal transition-colors shadow-[0_16px_40px_-12px_color-mix(in_oklab,var(--chili)_70%,transparent)] disabled:opacity-60">
                    {status === "submitting" ? "Sending…" : f(form, "submit_label", "Submit application")}
                    <span className="transition-transform group-hover:translate-x-0.5">→</span>
                  </button>

                </div>
              </form>
            )}
          </div>
        </div>
      </div>

      <style>{`
        .input {
          background: color-mix(in oklab, var(--charcoal) 60%, transparent);
          border: 1px solid color-mix(in oklab, var(--cream) 10%, transparent);
          padding: 0.95rem 1.1rem;
          color: var(--cream);
          font-family: var(--font-sans);
          font-size: 14px;
          width: 100%;
          outline: none;
          border-radius: 0.75rem;
          transition: border-color .2s, background .2s, box-shadow .2s;
        }
        .input::placeholder { color: color-mix(in oklab, var(--cream) 30%, transparent); }
        .input:hover { border-color: color-mix(in oklab, var(--cream) 18%, transparent); }
        .input:focus {
          border-color: var(--yellow);
          background: color-mix(in oklab, var(--charcoal) 80%, transparent);
          box-shadow: 0 0 0 4px color-mix(in oklab, var(--yellow) 12%, transparent);
        }
      `}</style>
    </section>
  );
}

function Field({ label, error, hint, children }: { label: string; error?: string; hint?: string; children: React.ReactNode }) {
  return (
    <label className="block">
      <span className="flex items-center justify-between mb-2">
        <span className="text-[10px] uppercase tracking-[0.12em] text-cream/60 font-semibold">{label}</span>
        {hint && <span className="text-[10px] text-cream/40 font-mono">{hint}</span>}
      </span>
      {children}
      {error && <span className="mt-1.5 text-xs text-chili block">{error}</span>}
    </label>
  );
}
